/**
 * BrainBridgeTokensStore — web mirror of
 * `Sources/Store/BrainBridgeTokensStore.swift` (the LIVE path).
 *
 * The real bridge-token client. Talks directly to the Brain Worker's bridge-token
 * API, NOT Supabase/PostgREST — bridge tokens are a Worker-owned credential family
 * scoped to the caller's tenant.
 *
 * AUTH: every request carries `Authorization: Bearer brain_*` — the SAME durable
 * token BrainAuth holds in localStorage (it does NOT use the short-lived Supabase
 * JWT), exactly like BrainMemoriesStore. Constructed with a `brainToken` provider
 * (supplied by BrainAuth) so it never learns how the token is stored or refreshed.
 */

import type { BrainConfig } from '../config';
import {
  BridgeTokensStoreError,
  friendlyBridgeError,
  type BridgeTokenInfo,
  type BridgeTokensStore,
  type MintedBridgeToken,
} from './BridgeTokensStore';

interface BridgeTokensListResponse {
  tokens: BridgeTokenInfo[];
}

export class BrainBridgeTokensStore implements BridgeTokensStore {
  private readonly baseURL: string;
  private readonly brainToken: () => Promise<string | null>;

  /**
   * @param config      provides the Brain Worker base URL.
   * @param brainToken  returns the current `brain_*` bearer token (from BrainAuth).
   */
  constructor(config: BrainConfig, brainToken: () => Promise<string | null>) {
    this.baseURL = config.brainBaseURL;
    this.brainToken = brainToken;
  }

  async mint(opts?: { label?: string; executor?: string }): Promise<MintedBridgeToken> {
    const body: Record<string, string> = {};
    if (opts?.label) body.label = opts.label;
    if (opts?.executor) body.executor = opts.executor;
    return this.request<MintedBridgeToken>('/bridge-tokens', 'POST', body);
  }

  async list(): Promise<BridgeTokenInfo[]> {
    const payload = await this.request<BridgeTokensListResponse>('/bridge-tokens', 'GET');
    return payload.tokens ?? [];
  }

  async revoke(tokenId: string): Promise<void> {
    await this.request<{ revoked: boolean }>('/bridge-tokens/revoke', 'POST', { token_id: tokenId });
  }

  // MARK: - Internals

  private async request<T>(path: string, method: string, body?: unknown): Promise<T> {
    const token = await this.brainToken();
    if (!token) throw new BridgeTokensStoreError('No Brain token', 401);

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const res = await fetch(new URL(path, this.baseURL).toString(), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    if (!res.ok) {
      throw new BridgeTokensStoreError(friendlyBridgeError(res.status, text), res.status);
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new BridgeTokensStoreError('Unexpected Brain response: not JSON');
    }
  }
}
